/**
 * usePublicationWorkflow - Publication status management
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Handles publication status transitions with validation and audit logging.
 * 
 * CRITICAL RULE: Editing a published object must either:
 * 1. Create a revision (keeps published version intact)
 * 2. Return the object to review status
 * 
 * Published geometry is immutable once published.
 */

import { useCallback } from 'react';
import { 
  PUBLICATION_STATUS, 
  STATUS_COLORS, 
  VALID_TRANSITIONS,
  getValidNextStatuses,
  isValidTransition,
  getStatusLabel
} from '../constants/mapEditor';

// Re-export for convenience
export { PUBLICATION_STATUS, STATUS_COLORS };

export const usePublicationWorkflow = () => {
  /**
   * Check if geometry can be edited
   * Published geometry cannot be edited - must be unpublished or go to revision
   */
  const canEditGeometry = useCallback((overlay) => {
    if (!overlay) return false;
    
    const status = overlay.publication_status || PUBLICATION_STATUS.DRAFT;
    
    // Published objects cannot have geometry edited directly
    if (status === PUBLICATION_STATUS.PUBLISHED) {
      return false;
    }
    
    // Archived objects cannot be edited
    if (status === PUBLICATION_STATUS.ARCHIVED) {
      return false;
    }
    
    // Locked overlays cannot have geometry edited
    if (overlay.isLocked) {
      return false;
    }
    
    return true;
  }, []);
  
  /**
   * Check if metadata can be edited (name, category, notes)
   * Metadata can be edited in more states than geometry
   */
  const canEditMetadata = useCallback((overlay) => {
    if (!overlay) return false;
    
    const status = overlay.publication_status || PUBLICATION_STATUS.DRAFT;
    
    // Archived objects cannot be edited at all
    if (status === PUBLICATION_STATUS.ARCHIVED) {
      return false;
    }
    
    // All other states allow metadata editing
    return true;
  }, []);
  
  /**
   * Handle editing a published object
   * Returns the required action: 'unpublish_first' or 'create_revision'
   */
  const handlePublishedEdit = useCallback((overlay, editType) => {
    if (!overlay) return null;
    
    // Check if locked
    if (overlay.isLocked) {
      return {
        action: 'blocked',
        message: 'Overlay is locked. Unlock it first to edit.',
        newStatus: null
      };
    }
    
    const status = overlay.publication_status;
    if (status !== PUBLICATION_STATUS.PUBLISHED) return null;
    
    // For geometry edits, require unpublishing
    if (['move', 'resize', 'vertex_edit'].includes(editType)) {
      return {
        action: 'requires_unpublish',
        message: 'Published geometry cannot be modified. Unpublish first or create a revision.',
        newStatus: PUBLICATION_STATUS.REQUIRES_REVISION
      };
    }
    
    // For metadata edits on published items, warn but allow
    if (['rename', 'category_change', 'notes'].includes(editType)) {
      return {
        action: 'warn',
        message: 'Modifying published metadata. Consider if this requires review.',
        newStatus: null
      };
    }
    
    return null;
  }, []);
  
  /**
   * Get color for a status
   */
  const getStatusColor = useCallback((status) => {
    return STATUS_COLORS[status] || STATUS_COLORS[PUBLICATION_STATUS.DRAFT];
  }, []);
  
  /**
   * Validate status change and return result
   */
  const validateStatusChange = useCallback((overlay, newStatus) => {
    if (!overlay) {
      return { valid: false, error: 'No overlay provided' };
    }
    
    const currentStatus = overlay.publication_status || PUBLICATION_STATUS.DRAFT;
    
    if (currentStatus === newStatus) {
      return { valid: false, error: 'Already at this status' };
    }
    
    if (!isValidTransition(currentStatus, newStatus)) {
      return {
        valid: false,
        error: `Cannot transition from ${getStatusLabel(currentStatus)} to ${getStatusLabel(newStatus)}`,
        validOptions: getValidNextStatuses(currentStatus)
      };
    }
    
    return { valid: true };
  }, []);
  
  return {
    // Constants
    PUBLICATION_STATUS,
    STATUS_COLORS,
    
    // Validation
    isValidTransition,
    getValidNextStatuses,
    canEditGeometry,
    canEditMetadata,
    handlePublishedEdit,
    validateStatusChange,
    
    // Display
    getStatusLabel,
    getStatusColor
  };
};

export default usePublicationWorkflow;
